import { CurrencyCode } from "@/types/common.types";

export const PaymentMethodValues = [
  "bankTransfer",
  "cash",
  "free",
  "online",
  "invoice",
  "wechat",
] as const;
export type PaymentMethod = (typeof PaymentMethodValues)[number];

/**
 * The subset of {@link PaymentMethod} that license (purchased-license) endpoints
 * accept. `invoice` / `wechat` exist platform-wide but are not valid here, so
 * the license mappers are total over this narrower union (no runtime throws).
 */
export const LicensePaymentMethodValues = [
  "bankTransfer",
  "cash",
  "online",
  "free",
] as const satisfies readonly PaymentMethod[];
export type LicensePaymentMethod = (typeof LicensePaymentMethodValues)[number];

export const PaymentStatusValues = [
  "completed",
  "expired",
  "refunded",
  "canceled",
  "pending",
  "partiallyPaid",
  "failed",
] as const;

export type PaymentStatus = (typeof PaymentStatusValues)[number];

export type PercentageFee = {
  minValue: number | null;
  maxValue: number | null;
  percentageValue: number | null;
};

export type FixedFee = {
  value: number | null;
  minPercentage: number | null;
  maxPercentage: number | null;
};

export type Fee = PercentageFee | FixedFee;

export const FeeType = {
  FIXED: "fixed",
  PERCENTAGE: "percentage",
} as const;

export const FeeTypeValues = Object.values(FeeType);
export type FeeType = (typeof FeeTypeValues)[number];

export const FeeBasisValues = ["perEntry", "perPlayer"] as const;
export type FeeBasis = (typeof FeeBasisValues)[number];

export const ServiceTypeValues = ["entry", "license"] as const;
export type ServiceType = (typeof ServiceTypeValues)[number];

export type CommonFeesSegment = {
  range: {
    from: number;
    to: number;
  };
  id: number;
  serviceType: ServiceType;
  feeBasis: FeeBasis;
  currency: CurrencyCode;
  enableMonetization: boolean;
};

export type PercentageFeesSegment = CommonFeesSegment & {
  feeType: typeof FeeType.PERCENTAGE;
  tournated: PercentageFee;
  partner: PercentageFee;
};

export type FixedFeesSegment = CommonFeesSegment & {
  feeType: typeof FeeType.FIXED;
  tournated: FixedFee;
  partner: FixedFee;
};

export type FeesSegment = FixedFeesSegment | PercentageFeesSegment;

/**
 * One level's VAT on the Tournated service fee (platform, entity-per-platform or
 * league). One setting per level — it covers every service type that level
 * charges (entry + license at platform / entity level, entry only on a league).
 *
 * `rate: null` means the backend default (`DEFAULT_TOURNATED_FEE_VAT_RATE`)
 * applies while VAT is on.
 */
export type TournatedFeeVat = {
  isEnabled: boolean;
  rate: number | null;
};

/** Which level a league's inherited Tournated-fee VAT comes from (`none` = no level configured it). */
export const TournatedFeeVatSourceValues = ["federation", "platform", "none"] as const;
export type TournatedFeeVatSource = (typeof TournatedFeeVatSourceValues)[number];

/** A league's inherited VAT on the Tournated fee plus the level that supplied it. */
export type InheritedTournatedFeeVat = TournatedFeeVat & {
  source: TournatedFeeVatSource;
};
